import { useState } from 'react';
import { apiGet } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';

/** One row of GET /pool-score/admin/rankings (UsersService.listPoolRankings). */
interface PoolScoreBreakdown {
  sla: number;
  completion: number;
  satisfaction: number;
  responsiveness: number;
  volume: number;
  competency: number;
  profile: number;
}

interface PoolRankingItem {
  id: string;
  name?: string;
  email?: string;
  specialtyCode: string;
  specialtyLabel: string;
  segment: {
    country?: string;
    region?: string;
    locationLabel?: string;
  };
  rank: number;
  totalInSegment: number;
  score: number;
  breakdown: PoolScoreBreakdown;
  provisional: boolean;
  isOnline: boolean;
  onlineInSegment: number;
  verificationStatus?: string;
  poolSuspendedAt?: string | null;
  poolScoreUpdatedAt?: string;
}

interface PoolRankingsPage {
  items: PoolRankingItem[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const BREAKDOWN_LABELS: Record<keyof PoolScoreBreakdown, string> = {
  sla: 'SLA',
  completion: 'Completion',
  satisfaction: 'Satisfaction',
  responsiveness: 'Responsiveness',
  volume: 'Volume',
  competency: 'Competency exam',
  profile: 'Profile',
};
const BREAKDOWN_KEYS = Object.keys(BREAKDOWN_LABELS) as (keyof PoolScoreBreakdown)[];

function ScoreBar({ value }: { value: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div
        style={{
          width: '4.5rem',
          height: '0.4rem',
          borderRadius: '999px',
          background: 'var(--border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, value))}%`,
            height: '100%',
            background: value >= 70 ? 'var(--success)' : value >= 40 ? 'var(--warning)' : 'var(--danger)',
          }}
        />
      </div>
      <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: '2ch' }}>{value}</span>
    </div>
  );
}

/**
 * Admin visibility into WHY each specialist sits where they do in the matching pool —
 * GET /pool-score/admin/rankings (UsersService.listPoolRankings, docs/pool-ranking.md).
 * Distinct from PoolScoreConfigPage (which edits the algorithm's weights): this page
 * shows the algorithm's OUTPUT per specialist, filterable by specialty/location.
 *
 * Rank/total are always scoped to the specialist's own real segment (specialty +
 * jurisdiction) — filters narrow which segments are included, not how rank inside
 * a segment is computed (same rule the backend's getPoolPosition uses).
 */
export function PoolRankingsPage() {
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params = new URLSearchParams({ page: String(page), limit: '20' });
  if (specialtyCode.trim()) params.set('specialtyCode', specialtyCode.trim());
  if (country.trim()) params.set('country', country.trim());
  if (region.trim()) params.set('region', region.trim());

  const { data, loading, error } = useApiResource<PoolRankingsPage>(
    () => apiGet<PoolRankingsPage>(`/pool-score/admin/rankings?${params.toString()}`),
    [specialtyCode, country, region, page],
  );

  const applyFilters = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Pool Ranking</h1>
          <p>
            Every specialist in the matching pool, ranked within their own specialty + jurisdiction
            segment, with the full score breakdown behind their position — so a &ldquo;why am I
            ranked here&rdquo; question is answerable at a glance. See{' '}
            <code>docs/pool-ranking.md</code> for the algorithm.
          </p>
        </div>
      </div>

      <div className="section-toolbar" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <label>
          Specialty code{' '}
          <input
            type="text"
            placeholder="e.g. cardiology"
            value={specialtyCode}
            onChange={(e) => applyFilters(setSpecialtyCode)(e.target.value)}
          />
        </label>
        <label>
          Country{' '}
          <input
            type="text"
            placeholder="ISO code, e.g. US"
            value={country}
            onChange={(e) => applyFilters(setCountry)(e.target.value)}
          />
        </label>
        <label>
          Region{' '}
          <input
            type="text"
            placeholder="e.g. Florida"
            value={region}
            onChange={(e) => applyFilters(setRegion)(e.target.value)}
          />
        </label>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <DataTable
        id="pool-rankings"
        rows={data?.items}
        loading={loading && !data}
        empty="No specialists match these filters."
        noun="specialists"
        getRowKey={(item) => item.id}
        pagination={
          data
            ? {
                page: data.page,
                totalPages: data.totalPages,
                total: data.total,
                hasNext: data.hasNext,
                hasPrev: data.hasPrev,
                onPageChange: setPage,
                noun: 'specialists',
              }
            : undefined
        }
        expandedRow={(item) =>
          expandedId !== item.id ? null : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
                gap: '0.75rem',
                padding: '0.75rem',
              }}
            >
              {BREAKDOWN_KEYS.map((key) => (
                <div key={key}>
                  <div style={{ fontSize: '0.85em', marginBottom: '0.25rem' }}>
                    {BREAKDOWN_LABELS[key]}
                  </div>
                  <ScoreBar value={item.breakdown[key]} />
                </div>
              ))}
              <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                {item.onlineInSegment} online in segment · last recomputed{' '}
                {item.poolScoreUpdatedAt
                  ? new Date(item.poolScoreUpdatedAt).toLocaleString()
                  : 'never'}
              </div>
            </div>
          )
        }
        columns={[
          {
            key: 'rank',
            header: 'Rank',
            value: (item) => item.rank,
            width: '90px',
            render: (item) => `${item.rank} / ${item.totalInSegment}`,
          },
          {
            key: 'specialist',
            header: 'Specialist',
            value: (item) => item.name || item.id,
            render: (item) => (
              <>
                <div>{item.name || item.id}</div>
                <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>{item.email}</div>
              </>
            ),
          },
          { key: 'specialty', header: 'Specialty', value: (item) => item.specialtyLabel, filter: 'select' },
          {
            key: 'location',
            header: 'Location',
            value: (item) => item.segment.locationLabel || '—',
            filter: 'select',
          },
          {
            key: 'score',
            header: 'Score',
            value: (item) => item.score,
            render: (item) => (
              <>
                <strong>{item.score}</strong>
                {item.provisional && (
                  <div>
                    <StatusBadge label="Provisional (no case history)" tone="warn" />
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            value: (item) =>
              item.poolSuspendedAt ? 'Pool suspended' : item.isOnline ? 'Online' : 'Offline',
            filter: 'select',
            render: (item) =>
              item.poolSuspendedAt ? (
                <StatusBadge label="Pool suspended" tone="bad" />
              ) : item.isOnline ? (
                <StatusBadge label="Online" tone="good" />
              ) : (
                <StatusBadge label="Offline" tone="neutral" />
              ),
          },
          {
            key: 'actions',
            header: '',
            render: (item) => (
              <button
                className="btn"
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
              >
                {expandedId === item.id ? 'Hide breakdown' : 'Why this rank?'}
              </button>
            ),
          },
        ]}
      />

    </div>
  );
}
